import type { CrossDivPmoDivision } from '@cortex/core-types';
import type { LightTaskTestability } from '@cortex/domain-product-innovation-lab-types';
import { normalizeDateArray } from '../date-utils.js';
import type {
  Account,
  AccountListResponse,
  DeleteAccountResponse,
  MemberDetailParams,
  MemberDetailResponse,
  MemberDevelopPointsResponse,
  MemberGithubActivityByRepoResponse,
  MemberGithubActivityIndexParams,
  MemberGithubActivityIndexResponse,
  MemberGithubActivityLogResponse,
  MemberGithubActivityParams,
  MemberGithubActivityPartnersResponse,
  MemberGithubActivityPullRequestResponse,
  MemberGithubActivitySummaryResponse,
  MemberGithubActivityTimeseriesResponse,
  MemberListParams,
  MemberListResponse,
  MemberQuarterlyKpiParams,
  MemberQuarterlyKpiResponse,
  MemberWipPointsResponse,
  MemberOpenPointsResponse,
  MemberReleasesResponse,
  MemberTrendParams,
  MemberTrendResponse,
  SearchUsersResponse,
  UpdateAccountInput,
} from '../../types/api.js';
import type {
  DevelopPointTargetsResponse,
  SaveDevelopPointTargetInput,
} from '../../types/develop-point-target.js';
import type {
  ChildTicketResponse,
  IntegratedProduct,
  MemberProjectListResponse,
  MemberRequirementProjectListResponse,
  MemberProjectHistoryListResponse,
  MemberProjectHistoryLimit,
  MyProjectsBoardResponse,
  PerformanceAggMode,
  PerformanceAggregationResponse,
  PmoDeadlineUpdateInput,
  PmoDeadlineUpdateResponse,
  Project,
  ReviewerResourceListResponse,
  PmoLeakResponse,
  PmoOverdueResponse,
  PmoUpcomingResponse,
  ProjectListResponse,
  LightTaskPoolResponse,
  AssignedLightTaskResponse,
  LightTaskClaimResult,
  LightTaskTestabilityUpdateResponse,
  TicketLookupResponse,
} from '../../types/project.js';
import { request } from './_request.js';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business アカウント管理APIクライアント。getAccounts/searchUsers/updateAccount/deleteAccountメソッドでアカウントエンドポイントを呼び出し
 * @graph-connects /accounts [calls] アカウント一覧API
 * @graph-connects /accounts/search [calls] ユーザー検索API
 * @graph-connects /accounts/:email [calls] アカウント更新・削除API
 */
export const accountApi = {
  getAccounts: (): Promise<AccountListResponse> => {
    return request<AccountListResponse>('/accounts');
  },
  searchUsers: async (query: string, limit = 10): Promise<SearchUsersResponse> => {
    const params = new URLSearchParams({ q: query, limit: String(limit) });
    return request<SearchUsersResponse>(`/accounts/search?${params.toString()}`);
  },
  updateAccount: async (email: string, data: UpdateAccountInput): Promise<Account> => {
    return request<Account>(`/accounts/${encodeURIComponent(email)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  deleteAccount: async (email: string): Promise<DeleteAccountResponse> => {
    return request<DeleteAccountResponse>(`/accounts/${encodeURIComponent(email)}`, {
      method: 'DELETE',
    });
  },
  impersonate: async (
    email: string,
  ): Promise<{ success: boolean; email: string; name: string }> => {
    return request<{ success: boolean; email: string; name: string }>('/auth/impersonate', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },
};

/**
 * GitHub activity query string を作る。
 * @graph-connects none
 */
function buildGithubActivityQuery(
  params?: MemberGithubActivityParams | MemberGithubActivityIndexParams,
): string {
  const query = new URLSearchParams();
  if (params?.from) query.set('from', params.from);
  if (params?.to) query.set('to', params.to);
  if (params?.range) query.set('range', params.range);
  for (const repo of params?.repo ?? []) {
    query.append('repo', repo);
  }
  if (params && 'sort' in params && typeof params.sort === 'string') {
    query.set('sort', params.sort);
  }
  if (params?.excludeBots === false) query.set('excludeBots', 'false');
  if (params?.includeBotsOnly === true) query.set('includeBotsOnly', 'true');
  const queryString = query.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Aggregate query string を作る。logins はカンマ区切り。
 * @graph-connects none
 */
function buildGithubActivityAggregateQuery(
  logins: string[],
  params?: MemberGithubActivityParams,
): string {
  const base = buildGithubActivityQuery(params);
  const search = new URLSearchParams(base.startsWith('?') ? base.slice(1) : base);
  if (logins.length > 0) {
    search.set('logins', logins.map((login) => login.toLowerCase()).join(','));
  }
  const queryString = search.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business メンバー管理APIクライアント。getMembers/getMemberDetail/getMemberTrend/getMemberDevelopPoints/getMemberReleases/getMemberQuarterlyKpiメソッドでメンバーエンドポイントを呼び出し
 * @graph-connects /members [calls] メンバー一覧API
 * @graph-connects /members/:email [calls] メンバー詳細API
 * @graph-connects /members/:email/trend [calls] メンバートレンドAPI
 * @graph-connects /members/:email/develop-points [calls] メンバー開発ポイントAPI
 * @graph-connects /members/:email/releases [calls] メンバーリリースAPI
 * @graph-connects /members/:email/quarterly-kpi [calls] メンバー四半期KPI API
 * @graph-connects /members/:email/wip-points [calls] メンバー処理中pt明細API
 * @graph-connects /members/:email/develop-point-targets [calls] 開発pt 自己設定目標線の一覧取得・追加/編集・解除API
 * @graph-connects normalizeDateArray [calls] 日付フィールド正規化ユーティリティ呼び出し
 */
export const memberApi = {
  getMembers: async (params?: MemberListParams): Promise<MemberListResponse> => {
    const query = new URLSearchParams();
    if (params?.organization) query.set('organization', params.organization);
    const queryString = query.toString();
    return request<MemberListResponse>(`/members${queryString ? `?${queryString}` : ''}`);
  },
  getMemberDetail: async (email: string): Promise<MemberDetailResponse> => {
    return request<MemberDetailResponse>(`/members/${encodeURIComponent(email)}`);
  },
  getMemberTrend: async (
    email: string,
    params?: MemberTrendParams,
  ): Promise<MemberTrendResponse> => {
    const query = new URLSearchParams();
    if (params?.days) query.set('days', String(params.days));
    const queryString = query.toString();
    const res = await request<MemberTrendResponse>(
      `/members/${encodeURIComponent(email)}/trend${queryString ? `?${queryString}` : ''}`,
    );
    return {
      ...res,
      data: normalizeDateArray(res.data),
    };
  },
  getMemberDevelopPoints: async (
    email: string,
    params?: MemberDetailParams,
  ): Promise<MemberDevelopPointsResponse> => {
    const query = new URLSearchParams();
    if (params?.startDate) query.set('startDate', params.startDate);
    if (params?.endDate) query.set('endDate', params.endDate);
    if (params?.limit) query.set('limit', String(params.limit));
    const queryString = query.toString();
    const res = await request<MemberDevelopPointsResponse>(
      `/members/${encodeURIComponent(email)}/develop-points${queryString ? `?${queryString}` : ''}`,
    );
    return {
      ...res,
      data: normalizeDateArray(res.data),
    };
  },
  getMemberReleases: async (
    email: string,
    params?: MemberDetailParams,
  ): Promise<MemberReleasesResponse> => {
    const query = new URLSearchParams();
    if (params?.startDate) query.set('startDate', params.startDate);
    if (params?.endDate) query.set('endDate', params.endDate);
    if (params?.limit) query.set('limit', String(params.limit));
    const queryString = query.toString();
    const res = await request<MemberReleasesResponse>(
      `/members/${encodeURIComponent(email)}/releases${queryString ? `?${queryString}` : ''}`,
    );
    return {
      ...res,
      data: normalizeDateArray(res.data),
    };
  },
  getMemberProjects: (email: string): Promise<MemberProjectListResponse> => {
    return request<MemberProjectListResponse>(`/members/${encodeURIComponent(email)}/projects`);
  },
  getMemberRequirementProjects: (email: string): Promise<MemberRequirementProjectListResponse> => {
    return request<MemberRequirementProjectListResponse>(
      `/members/${encodeURIComponent(email)}/requirement-projects`,
    );
  },
  getMemberProjectHistory: (
    email: string,
    limit: MemberProjectHistoryLimit = 30,
  ): Promise<MemberProjectHistoryListResponse> => {
    return request<MemberProjectHistoryListResponse>(
      `/members/${encodeURIComponent(email)}/project-history?limit=${String(limit)}`,
    );
  },
  getMemberQuarterlyKpi: (
    email: string,
    params?: MemberQuarterlyKpiParams,
  ): Promise<MemberQuarterlyKpiResponse> => {
    const query = new URLSearchParams();
    if (params?.quarter) query.set('quarter', params.quarter);
    const queryString = query.toString();
    return request<MemberQuarterlyKpiResponse>(
      `/members/${encodeURIComponent(email)}/quarterly-kpi${queryString ? `?${queryString}` : ''}`,
    );
  },
  getMemberWipPoints: (email: string): Promise<MemberWipPointsResponse> => {
    return request<MemberWipPointsResponse>(`/members/${encodeURIComponent(email)}/wip-points`);
  },
  getMemberOpenPoints: (email: string): Promise<MemberOpenPointsResponse> => {
    return request<MemberOpenPointsResponse>(`/members/${encodeURIComponent(email)}/open-points`);
  },
  getMemberDevelopPointTargets: (email: string): Promise<DevelopPointTargetsResponse> => {
    return request<DevelopPointTargetsResponse>(
      `/members/${encodeURIComponent(email)}/develop-point-targets`,
    );
  },
  saveMemberDevelopPointTarget: (
    email: string,
    input: SaveDevelopPointTargetInput,
  ): Promise<DevelopPointTargetsResponse> => {
    return request<DevelopPointTargetsResponse>(
      `/members/${encodeURIComponent(email)}/develop-point-targets`,
      { method: 'PUT', body: JSON.stringify(input) },
    );
  },
  deleteMemberDevelopPointTarget: (
    email: string,
    id: string,
  ): Promise<DevelopPointTargetsResponse> => {
    return request<DevelopPointTargetsResponse>(
      `/members/${encodeURIComponent(email)}/develop-point-targets/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
  },
  getGithubSummary: (
    login: string,
    params?: MemberGithubActivityParams,
  ): Promise<MemberGithubActivitySummaryResponse> => {
    return request<MemberGithubActivitySummaryResponse>(
      `/members/${encodeURIComponent(login)}/github/summary${buildGithubActivityQuery(params)}`,
    );
  },
  getGithubTimeseries: (
    login: string,
    params?: MemberGithubActivityParams,
  ): Promise<MemberGithubActivityTimeseriesResponse> => {
    return request<MemberGithubActivityTimeseriesResponse>(
      `/members/${encodeURIComponent(login)}/github/timeseries${buildGithubActivityQuery(params)}`,
    );
  },
  getGithubByRepo: (
    login: string,
    params?: MemberGithubActivityParams,
  ): Promise<MemberGithubActivityByRepoResponse> => {
    return request<MemberGithubActivityByRepoResponse>(
      `/members/${encodeURIComponent(login)}/github/by-repo${buildGithubActivityQuery(params)}`,
    );
  },
  getGithubPartners: (
    login: string,
    params?: MemberGithubActivityParams,
  ): Promise<MemberGithubActivityPartnersResponse> => {
    return request<MemberGithubActivityPartnersResponse>(
      `/members/${encodeURIComponent(login)}/github/partners${buildGithubActivityQuery(params)}`,
    );
  },
  getGithubActivities: (
    login: string,
    params?: MemberGithubActivityParams,
  ): Promise<MemberGithubActivityLogResponse> => {
    return request<MemberGithubActivityLogResponse>(
      `/members/${encodeURIComponent(login)}/github/activities${buildGithubActivityQuery(params)}`,
    );
  },
  getGithubPullRequest: (
    login: string,
    id: string,
  ): Promise<MemberGithubActivityPullRequestResponse> => {
    return request<MemberGithubActivityPullRequestResponse>(
      `/members/${encodeURIComponent(login)}/github/prs/${encodeURIComponent(id)}`,
    );
  },
  getGithubIndex: (
    params?: MemberGithubActivityIndexParams,
  ): Promise<MemberGithubActivityIndexResponse> => {
    return request<MemberGithubActivityIndexResponse>(
      `/members/github/index${buildGithubActivityQuery(params)}`,
    );
  },
  getGithubAggregateSummary: (
    logins: string[],
    params?: MemberGithubActivityParams,
  ): Promise<MemberGithubActivitySummaryResponse> => {
    return request<MemberGithubActivitySummaryResponse>(
      `/members/github/aggregate/summary${buildGithubActivityAggregateQuery(logins, params)}`,
    );
  },
  getGithubAggregateTimeseries: (
    logins: string[],
    params?: MemberGithubActivityParams,
  ): Promise<MemberGithubActivityTimeseriesResponse> => {
    return request<MemberGithubActivityTimeseriesResponse>(
      `/members/github/aggregate/timeseries${buildGithubActivityAggregateQuery(logins, params)}`,
    );
  },
  getGithubAggregateByRepo: (
    logins: string[],
    params?: MemberGithubActivityParams,
  ): Promise<MemberGithubActivityByRepoResponse> => {
    return request<MemberGithubActivityByRepoResponse>(
      `/members/github/aggregate/by-repo${buildGithubActivityAggregateQuery(logins, params)}`,
    );
  },
  getGithubAggregatePartners: (
    logins: string[],
    params?: MemberGithubActivityParams,
  ): Promise<MemberGithubActivityPartnersResponse> => {
    return request<MemberGithubActivityPartnersResponse>(
      `/members/github/aggregate/partners${buildGithubActivityAggregateQuery(logins, params)}`,
    );
  },
  getGithubAggregateActivities: (
    logins: string[],
    params?: MemberGithubActivityParams,
  ): Promise<MemberGithubActivityLogResponse> => {
    return request<MemberGithubActivityLogResponse>(
      `/members/github/aggregate/activities${buildGithubActivityAggregateQuery(logins, params)}`,
    );
  },
};

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business プロジェクトAPIクライアント。getProjects/getChildTickets/getProjectメソッドでプロジェクトエンドポイントを呼び出し
 * @graph-connects /projects [calls] プロジェクト一覧API
 * @graph-connects /projects/:parentId/children [calls] 子チケット一覧API
 * @graph-connects /projects/:pjKey [calls] 案件単体取得API
 */
export const projectApi = {
  /**
   * プロジェクト一覧を取得する。division を指定すると指定 Div（aC/Mall/Logi Div看板）で
   * スコープした案件を返す（VIEW_PMO_BOARD 権限が必要）。未指定時はログインユーザーの
   * 所属に応じたスコープ（PI Div=全社 / 横断 PMO=自 Div）で返す。
   */
  getProjects: (division?: CrossDivPmoDivision): Promise<ProjectListResponse> => {
    const query = division ? `?division=${division}` : '';
    return request<ProjectListResponse>(`/projects${query}`);
  },
  getChildTickets: (parentId: number): Promise<ChildTicketResponse> => {
    return request<ChildTicketResponse>(`/projects/${String(parentId)}/children`);
  },
  /** 案件詳細（ダッシュボードタブ）: Backlog 課題キーから案件 1 件を取得する */
  getProject: (pjKey: string): Promise<Project> => {
    return request<Project>(`/projects/${encodeURIComponent(pjKey)}`);
  },
  /**
   * 統合プロダクト詳細（Gen1案件詳細ダッシュボードタブ）: Backlog 課題キーから
   * Gen1統合プロダクト1件 + 配下のGen2チャンク一覧を取得する
   */
  getIntegratedProduct: (pjKey: string): Promise<IntegratedProduct> => {
    return request<IntegratedProduct>(`/projects/${encodeURIComponent(pjKey)}/integrated-product`);
  },
  /** PMO看板: 結合期限切れ・結合完了日未設定の進行中Gen2プロジェクト一覧 */
  getPmoOverdue: (): Promise<PmoOverdueResponse> => {
    return request<PmoOverdueResponse>('/projects/pmo-board/overdue');
  },
  /** PMO看板: 今日から指定日数以内に結合期限を迎える、結合完了日未設定の進行中Gen2プロジェクト一覧 */
  getPmoUpcoming: (daysAhead = 3): Promise<PmoUpcomingResponse> => {
    return request<PmoUpcomingResponse>(`/projects/pmo-board/upcoming?days=${String(daysAhead)}`);
  },
  /** PMO看板: リリース日漏れ管理（リリース完了日未設定 + 親チケ完了 or リリース子チケ完了） */
  getPmoReleaseLeaks: (): Promise<PmoLeakResponse> => {
    return request<PmoLeakResponse>('/projects/pmo-board/release-leaks');
  },
  /** PMO看板: アサイン日漏れ管理（アサイン決定日未設定 + リリース済 or 未対応/保留中以外） */
  getPmoAssignLeaks: (): Promise<PmoLeakResponse> => {
    return request<PmoLeakResponse>('/projects/pmo-board/assign-leaks');
  },
  /** PMO看板: 期限カスタムフィールドを Backlog 本体へ書き戻す */
  updateDeadlines: (
    issueKey: string,
    body: PmoDeadlineUpdateInput,
  ): Promise<PmoDeadlineUpdateResponse> => {
    return request<PmoDeadlineUpdateResponse>(
      `/projects/${encodeURIComponent(issueKey)}/deadlines`,
      {
        method: 'PATCH',
        body: JSON.stringify(body),
      },
    );
  },
  /** PL/主担当看板: ログインユーザーが PL または 主担当 になっている進行中Gen2案件一覧 */
  getMyProjectsBoard: (): Promise<MyProjectsBoardResponse> => {
    return request<MyProjectsBoardResponse>('/projects/my-projects');
  },
  /** 軽タスクリスト（全社共有プール）: 軽タスク認定 + 開発リーダー未設定の案件を起票時刻の昇順で取得 */
  getLightTaskPool: (): Promise<LightTaskPoolResponse> => {
    return request<LightTaskPoolResponse>('/projects/light-tasks');
  },
  /** アサイン済み軽タスク一覧: 軽タスク認定・開発リーダー設定済み・未完了の案件を起票時刻の昇順で取得 */
  getAssignedLightTasks: (): Promise<AssignedLightTaskResponse> => {
    return request<AssignedLightTaskResponse>('/projects/light-tasks/assigned');
  },
  /** 軽タスク セルフ・アサイン: 認証ユーザーの所属に合う軽タスクを起票順に1件アサインする */
  claimLightTask: (): Promise<LightTaskClaimResult> => {
    return request<LightTaskClaimResult>('/projects/light-tasks/claim', { method: 'POST' });
  },
  /**
   * 軽タスクのテスト可否を保存する（null で未設定に戻す）。cortex 側の Firestore にのみ
   * 保存し、Backlog 等の他システムへは書き戻さない。
   */
  updateLightTaskTestability: (
    issueKey: string,
    testability: LightTaskTestability | null,
  ): Promise<LightTaskTestabilityUpdateResponse> => {
    return request<LightTaskTestabilityUpdateResponse>(
      `/projects/light-tasks/${encodeURIComponent(issueKey)}/testability`,
      { method: 'PUT', body: JSON.stringify({ testability }) },
    );
  },
  /**
   * レビュアーリソース: 現在コードレビューを担当しているエンジニアごとの
   * レビュー案件数 / レビュー予定pt / 今日起点1ヶ月の capacity / レビュー割合(%) を
   * ニックネーム昇順で取得する。
   */
  getReviewerResources: (): Promise<ReviewerResourceListResponse> => {
    return request<ReviewerResourceListResponse>('/projects/reviewer-resources');
  },
  /** PMO看板 実績集計: 指定期間(完了更新日基準)の完了pt実績を母票の事業単位で集計 */
  getPerformanceAggregation: (
    mode: PerformanceAggMode,
    startDate: string,
    endDate: string,
  ): Promise<PerformanceAggregationResponse> => {
    const query = new URLSearchParams({ mode, startDate, endDate });
    return request<PerformanceAggregationResponse>(
      `/projects/pmo-board/performance-agg?${query.toString()}`,
    );
  },
  /**
   * チケット一括照会: 案件番号（`AIRCLOSET-137048` / `137048` / 課題 URL）から
   * 案件名・ステータス・種別（開発のみ/デザインあり）・ざっくり見積の平均pt をまとめて引く。
   */
  lookupTickets: (tokens: string[]): Promise<TicketLookupResponse> => {
    return request<TicketLookupResponse>('/projects/ticket-lookup', {
      method: 'POST',
      body: JSON.stringify({ tokens }),
    });
  },
};
